import {
  exhibitorTypeOptions,
  schoolControlOptions,
  schoolLevelOptions,
  schoolSizeOptions,
} from "components/use-query-params-filters"
import useCurrent from "lib/use-current"
import { useStateMachine } from "little-state-machine"
import { Filter } from "models/convention-api/types"
import { useRouter } from "next/router"
import { useMemo } from "react"
import { usaStates } from "typed-usa-states"
import { isNilOrEmpty } from "utils/helpers"

interface SessionsFilter {
  filters: Filter
  activeFilters: number
}

export const useSessionsFilter = (): SessionsFilter => {
  const router = useRouter()
  const conventionId = router.query.convention_id as string

  const { state } = useStateMachine()
  const current = useCurrent()

  // @ts-ignore PT - #180556963
  const providerConfig = current?.config

  const filters = useMemo(() => {
    const {
      when,
      when_time,
      tags,
      exhibitorName,
      school_size,
      school_control,
      school_level,
      exhibitor_type,
      majors,
      states,
      international,
    } = state.sessionsFiltersStore

    return removeInactiveFilters({
      when,
      when_time: decodeURIComponent(when_time),
      tags,
      convention_id: conventionId,
      institution_name: exhibitorName,
      school_size: filterOptions(schoolSizeOptions, school_size),
      school_control: filterOptions(schoolControlOptions, school_control),
      school_level: filterOptions(schoolLevelOptions, school_level),
      exhibitor_type: filterOptions(exhibitorTypeOptions, exhibitor_type),
      programs: majors.map(({ id }) => id).filter(Boolean),
      states: states
        .map((stateName) => {
          const foundState = usaStates.find((state) => state.name === stateName)
          if (!foundState) return ""

          return foundState.abbreviation
        })
        .filter(Boolean),
      territory: international ? "international" : "",
    })
  }, [state.sessionsFiltersStore, conventionId])

  const activeFilters = useMemo(() => Object.keys(filters).length, [filters])

  return useMemo(
    () => ({
      filters,
      activeFilters,
    }),
    [filters, activeFilters],
  )
}

// HELPERS

const removeInactiveFilters = (filters: Filter): Filter =>
  Object.fromEntries(
    Object.entries(filters).filter(([, filterValue]) => !isNilOrEmpty(filterValue)),
  )

const filterOptions = <T extends { value: unknown }>(
  baseOptions: T[],
  filterByOptions: boolean[],
): T["value"][] =>
  baseOptions.filter((_, index) => filterByOptions[index]).map(({ value }) => value)
